import React, { useState, useEffect } from 'react';
import { 
  Plus,
  Edit2,
  Trash2,
  GripVertical,
  Save,
  X,
  AlertCircle,
  CheckCircle,
  Settings
} from 'lucide-react';

const SurveyQuestionManager = ({ API_BASE_URL, success, error }) => {
  const [activeSurveyType, setActiveSurveyType] = useState('onboarding');
  const [questions, setQuestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [formData, setFormData] = useState({
    question_text: '',
    question_type: 'text',
    is_required: false,
    options: []
  });

  const surveyTypes = {
    onboarding: { label: 'Onboarding', color: 'blue' },
    checkin: { label: 'Check-in', color: 'purple' },
    offboarding: { label: 'Offboarding', color: 'orange' }
  };

  const questionTypes = [
    { value: 'text', label: 'Short Text' },
    { value: 'textarea', label: 'Long Text' },
    { value: 'radio', label: 'Multiple Choice (Single)' },
    { value: 'checkbox', label: 'Multiple Choice (Multiple)' },
    { value: 'rating', label: 'Rating Scale' }
  ];

  useEffect(() => {
    fetchQuestions();
  }, [activeSurveyType]);

  const fetchQuestions = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}/survey_questions.php?action=getQuestions&survey_type=${activeSurveyType}`
      );
      const data = await response.json();
      
      if (data.success) {
        setQuestions(data.questions);
      }
    } catch (err) {
      console.error('Error fetching questions:', err);
      error('Failed to load questions');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveQuestion = async () => {
    if (!formData.question_text.trim()) {
      error('Question text is required');
      return;
    }

    const questionData = {
      ...formData,
      survey_type: activeSurveyType,
      question_order: editingQuestion ? editingQuestion.question_order : questions.length + 1
    };

    if (editingQuestion) {
      questionData.id = editingQuestion.id;
    }

    try {
      const action = editingQuestion ? 'updateQuestion' : 'addQuestion';
      const response = await fetch(`${API_BASE_URL}/survey_questions.php?action=${action}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(questionData)
      });

      const data = await response.json();
      
      if (data.success) {
        success(editingQuestion ? 'Question updated!' : 'Question added!');
        setShowAddModal(false);
        setEditingQuestion(null);
        resetForm();
        fetchQuestions();
      } else {
        error(data.message);
      }
    } catch (err) {
      error('Failed to save question');
    }
  };

  const handleDeleteQuestion = async (questionId) => {
    if (!window.confirm('Are you sure you want to delete this question?')) return;

    try {
      const response = await fetch(`${API_BASE_URL}/survey_questions.php?action=deleteQuestion`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: questionId })
      });

      const data = await response.json();
      
      if (data.success) {
        success('Question deleted!');
        fetchQuestions();
      } else {
        error(data.message);
      }
    } catch (err) {
      error('Failed to delete question');
    }
  };

  const resetForm = () => {
    setFormData({
      question_text: '',
      question_type: 'text',
      is_required: false,
      options: []
    });
  };

  const addOption = () => {
    setFormData({
      ...formData,
      options: [...formData.options, '']
    });
  };

  const updateOption = (index, value) => {
    const newOptions = [...formData.options];
    newOptions[index] = value;
    setFormData({ ...formData, options: newOptions });
  };

  const removeOption = (index) => {
    const newOptions = formData.options.filter((_, i) => i !== index);
    setFormData({ ...formData, options: newOptions });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-br from-slate-50 to-blue-50 rounded-2xl p-6 border border-slate-200">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-purple-600 rounded-xl flex items-center justify-center">
              <Settings className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-slate-900">Survey Question Manager</h2>
              <p className="text-sm text-slate-600 mt-1">Manage questions for each survey type</p>
            </div>
          </div>
          <button
            onClick={() => {
              setShowAddModal(true);
              setEditingQuestion(null);
              resetForm();
            }}
            className="flex items-center px-4 py-2.5 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl hover:from-blue-700 hover:to-purple-700 transition-all font-medium shadow-lg shadow-blue-500/30 hover:shadow-xl hover:shadow-blue-500/40 transform hover:scale-105"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Question
          </button>
        </div>

        {/* Survey Type Tabs */}
        <div className="flex space-x-2">
          {Object.entries(surveyTypes).map(([type, config]) => (
            <button
              key={type}
              onClick={() => setActiveSurveyType(type)}
              className={`px-6 py-3 rounded-xl font-medium transition-all ${
                activeSurveyType === type
                  ? `bg-gradient-to-r from-${config.color}-500 to-${config.color}-600 text-white shadow-lg`
                  : 'bg-white text-slate-600 border-2 border-slate-200 hover:border-slate-300'
              }`}
            >
              {config.label}
            </button>
          ))}
        </div>
      </div>

      {/* Questions List */}
      <div className="bg-white rounded-xl border-2 border-slate-200 p-6">
        {isLoading ? (
          <div className="text-center py-12">
            <div className="w-12 h-12 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mx-auto"></div>
            <p className="text-sm text-slate-600 mt-4">Loading questions...</p>
          </div>
        ) : questions.length === 0 ? (
          <div className="text-center py-12">
            <AlertCircle className="w-12 h-12 text-slate-400 mx-auto mb-3" />
            <p className="text-slate-600 font-medium">No questions yet</p>
            <p className="text-sm text-slate-500 mt-1">Click "Add Question" to create your first question</p>
          </div>
        ) : (
          <div className="space-y-4">
            {questions.map((question, index) => (
              <div
                key={question.id}
                className="flex items-start space-x-4 p-5 bg-gradient-to-br from-slate-50 to-blue-50/30 rounded-xl border-2 border-slate-200 hover:border-blue-300 hover:shadow-md transition-all"
              >
                <div className="flex-shrink-0 mt-1 cursor-move">
                  <GripVertical className="w-5 h-5 text-slate-400" />
                </div>
                
                <div className="flex-1">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center space-x-3">
                      <span className="flex-shrink-0 w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center text-sm font-bold text-white shadow-md">
                        {index + 1}
                      </span>
                      <h4 className="font-semibold text-slate-900 text-lg">{question.question_text}</h4>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => {
                          setEditingQuestion(question);
                          setFormData({
                            question_text: question.question_text,
                            question_type: question.question_type,
                            is_required: question.is_required,
                            options: question.options || []
                          });
                          setShowAddModal(true);
                        }}
                        className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors"
                        title="Edit Question"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteQuestion(question.id)}
                        className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                        title="Delete Question"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-4 text-sm">
                    <span className="px-3 py-1.5 bg-white rounded-lg border-2 border-slate-200 text-slate-700 font-medium">
                      {questionTypes.find(t => t.value === question.question_type)?.label}
                    </span>
                    {question.is_required && (
                      <span className="px-3 py-1.5 bg-red-50 text-red-700 rounded-lg border-2 border-red-200 font-medium">
                        Required
                      </span>
                    )}
                    {question.options && question.options.length > 0 && (
                      <span className="text-slate-500 font-medium">
                        {question.options.length} options
                      </span>
                    )}
                  </div>

                  {question.options && question.options.length > 0 && (
                    <div className="mt-4 ml-4 space-y-2">
                      {question.options.map((option, idx) => (
                        <div key={idx} className="flex items-center space-x-2 text-sm text-slate-600">
                          <div className="w-2 h-2 bg-gradient-to-br from-blue-400 to-purple-500 rounded-full"></div>
                          <span className="bg-white px-3 py-1.5 rounded-lg border border-slate-200">{option}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add/Edit Question Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden shadow-2xl">
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-6">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-white">
                  {editingQuestion ? 'Edit Question' : 'Add New Question'}
                </h3>
                <button
                  onClick={() => {
                    setShowAddModal(false);
                    setEditingQuestion(null);
                    resetForm();
                  }}
                  className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-white" />
                </button>
              </div>
            </div>

            <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
              <div className="space-y-6">
                {/* Question Text */}
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Question Text *
                  </label>
                  <textarea
                    value={formData.question_text}
                    onChange={(e) => setFormData({ ...formData, question_text: e.target.value })}
                    rows={3}
                    className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 transition-all"
                    placeholder="Enter your question..."
                  />
                </div>

                {/* Question Type */}
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Question Type
                  </label>
                  <select
                    value={formData.question_type}
                    onChange={(e) => setFormData({ ...formData, question_type: e.target.value, options: [] })}
                    className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 transition-all font-medium"
                  >
                    {questionTypes.map(type => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                </div>

                {/* Required Checkbox */}
                <div className="flex items-center p-4 bg-slate-50 rounded-xl border-2 border-slate-200">
                  <input
                    type="checkbox"
                    id="required"
                    checked={formData.is_required}
                    onChange={(e) => setFormData({ ...formData, is_required: e.target.checked })}
                    className="w-5 h-5 text-blue-600 rounded border-slate-300 focus:ring-2 focus:ring-blue-500"
                  />
                  <label htmlFor="required" className="ml-3 text-sm font-medium text-slate-700">
                    Make this question required
                  </label>
                </div>

                {/* Options (for radio/checkbox types) */}
                {(formData.question_type === 'radio' || formData.question_type === 'checkbox') && (
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <label className="block text-sm font-semibold text-slate-700">
                        Answer Options
                      </label>
                      <button
                        onClick={addOption}
                        className="flex items-center px-3 py-2 text-sm bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors font-medium"
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        Add Option
                      </button>
                    </div>
                    <div className="space-y-2">
                      {formData.options.map((option, index) => (
                        <div key={index} className="flex items-center space-x-2">
                          <input
                            type="text"
                            value={option}
                            onChange={(e) => updateOption(index, e.target.value)}
                            className="flex-1 px-4 py-2.5 border-2 border-slate-200 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10 transition-all"
                            placeholder={`Option ${index + 1}`}
                          />
                          <button
                            onClick={() => removeOption(index)}
                            className="p-2.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          >
                            <X className="w-5 h-5" />
                          </button>
                        </div>
                      ))}
                      {formData.options.length === 0 && (
                        <div className="text-center py-8 bg-slate-50 rounded-xl border-2 border-dashed border-slate-300">
                          <p className="text-sm text-slate-500">No options yet. Click "Add Option" to create options.</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="border-t-2 border-slate-200 p-6 bg-slate-50">
              <div className="flex items-center justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowAddModal(false);
                    setEditingQuestion(null);
                    resetForm();
                  }}
                  className="px-5 py-2.5 border-2 border-slate-300 text-slate-700 rounded-xl hover:bg-slate-100 transition-all font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveQuestion}
                  disabled={!formData.question_text.trim()}
                  className="flex items-center px-6 py-2.5 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl hover:from-blue-700 hover:to-purple-700 transition-all font-medium shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {editingQuestion ? 'Update' : 'Add'} Question
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SurveyQuestionManager;